import { httpRequest } from '../../shared/lib/utils/httpClient';
import { rowsPerPage } from '../../pages/constants';
import type { ErrorResponse } from '@qler-view/shared_lib/src/shared/lib/utils/httpClient.js';

export interface FacilityUserList {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  facilityUsersList: FacilityUser[];
}

export interface FacilityUser {
  id: number;
  facilityUserBusinessId: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  assignedTo: string | null;
  status: string | null;
  facilityName: string | null;
  role: string | null;
  primaryEmail: string | null;
  createdOn: string | null;
  isLocked: boolean;
}

export interface FacilityUserData {
  id: number;
  firstName: string;
  lastName: string;
  status: boolean;
  primaryEmail: string;
  username: string;
  roleId: number;
  password: string;
  facilityId: number;
  managedFacilityIds: ActiveFacilities[];
  notes: string;
  mfaEnabledByAdmin: boolean;
  isLocked: boolean;
}

export interface Roles {
  id: number;
  name: string;
}

export interface ActiveFacilities {
  id: number;
  name: string;
}

export interface FacilityUserMetaData {
  roles: Roles[];
  activeFacilities: ActiveFacilities[];
}

export interface UniqResponse {
  code: number;
  status: string;
  message: string;
}

export type EmailCheckResult = {
  isAvailable: boolean;
  message?: string;
};

type GetFacilityUserListParams = {
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  facilityId?: number[];
  statusId?: string;
  roleId?: number[];
};

export type UnBlockPayload = {
  facilityUserId: number;
};

export const getFacilityUserList = async (
  options: GetFacilityUserListParams = {}
): Promise<FacilityUserList> => {
  const {
    page = 0,
    size = rowsPerPage,
    sortField,
    sortType,
    searchTerm,
    facilityId,
    statusId,
    roleId,
  } = options;

  const params: Record<string, string | number> = {
    page,
    size,
  };

  const userId = localStorage.getItem('userId') ?? '0';

  if (sortField) params.sortField = sortField;
  if (sortType) params.sortType = sortType;
  if (searchTerm) params.searchTerm = searchTerm;
  if (statusId && statusId !== '0') params.statusId = statusId;
  if (facilityId?.length) params.facilityId = facilityId.join(',');
  if (roleId?.length) params.roleId = roleId.join(',');

  const response = await httpRequest<FacilityUserList>({
    method: 'GET',
    url: '/operation/facility-users',
    params,
    headers: { userId },
  });

  return response;
};

export const getFacilityUserMetaData = async (
  userId: string
): Promise<FacilityUserMetaData> => {
  const response = await httpRequest<FacilityUserMetaData>({
    method: 'GET',
    url: `/operation/facility-user/metadata`,
    headers: { userId },
  });
  return response;
};

export const saveOrUpdateFacilityUser = async (
  userId: string,
  action: string,
  payload: FacilityUserData
) => {
  const apiAction = action === 'Save' ? 'SAVE' : 'UPDATE';
  const response = await httpRequest<FacilityUserData>({
    method: 'PUT',
    url: `/operation/facility-user?action=${apiAction}`,
    headers: { userId },
    data: payload,
  });
  return response;
};

export const getFacilityUserDetailsById = async (
  userId: string,
  facilityUserId: number
): Promise<FacilityUserData> => {
  const response = await httpRequest<FacilityUserData>({
    method: 'GET',
    url: `/operation/facility-user?facilityUserId=${facilityUserId}`,
    headers: { userId },
  });
  return response;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleError = (error: any): EmailCheckResult => {
  if (error.status === 409 || error.status === 402) {
    console.log('APIerror', error.response);

    return {
      isAvailable: false,
      message: (error?.data as ErrorResponse).message,
    };
  }

  throw error;
};

export const checkEmailUniq = async (
  primaryEmail: string,
  facilityUserId?: number
): Promise<EmailCheckResult> => {
  const params = new URLSearchParams({
    primaryEmail,
    ...(facilityUserId !== undefined && {
      facilityUserId: facilityUserId.toString(),
    }),
  });

  try {
    const response = await httpRequest<EmailCheckResult>({
      method: 'GET',
      url: `/operation/facility-user/primary-email/available?${params.toString()}`,
    });

    return response ?? { isAvailable: true };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return handleError(error);
  }
};

export const checkUsernameUniq = async (
  username: string,
  facilityUserId?: number
): Promise<EmailCheckResult> => {
  const params = new URLSearchParams({
    username,
    ...(facilityUserId !== undefined && {
      facilityUserId: facilityUserId.toString(),
    }),
  });

  try {
    const response = await httpRequest<EmailCheckResult>({
      method: 'GET',
      url: `/operation/facility-user/username/available?${params.toString()}`,
    });

    return response ?? { isAvailable: true };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return handleError(error);
  }
};

export const unBlockFacilityUser = async (
  userId: string,
  payload: UnBlockPayload
) => {
  const response = await httpRequest<UnBlockPayload>({
    method: 'PUT',
    url: `/operation/facility-user/unblock`,
    headers: { userId },
    data: payload,
  });
  return response;
};
